import type { LotEvent, LotLocationType } from '../../types/lot'

export type ProcessingSiteRecentLotProvenance = {
  eventType: 'move' | 'transfer'
  recordedAt: string
  fromLocationType?: LotLocationType
  fromLocationId?: string
  fromFarmId?: string
  fromProductionUnitId?: string
}

export type ProcessingSiteActivityEntry = {
  lotId: string
  eventType: 'move' | 'transfer'
  recordedAt: string
  fromLocationType?: LotLocationType
  fromLocationId?: string
  fromFarmId?: string
  fromProductionUnitId?: string
}

export type ProcessingSiteActivityOriginSummaryEntry = {
  fromLocationType?: LotLocationType
  fromLocationId?: string
  fromFarmId?: string
  fromProductionUnitId?: string
  totalCount: number
  transferCount: number
  moveCount: number
  latestRecordedAt: string
}

export type ProcessingSiteActivityRecencySummary = {
  totalEntries: number
  last24HoursCount: number
  last7DaysCount: number
  latestRecordedAt: string | null
  isStale: boolean
}

export type ProcessingSiteActivityOriginRecencyBadge = 'last24h' | 'last7d' | 'stale' | 'unknown'

export type ProcessingSiteActivityOriginRecencyBreakdown = {
  totalOrigins: number
  last24HoursCount: number
  last7DaysCount: number
  staleCount: number
  unknownCount: number
  hasStaleOrigins: boolean
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  values.forEach((value) => {
    const normalized = String(value ?? '').trim()
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    out.push(normalized)
  })
  return out
}

const asObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

const readOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : undefined
}

const compareRecordedAtDesc = (left: Pick<LotEvent, 'recordedAt'>, right: Pick<LotEvent, 'recordedAt'>): number => {
  const leftTimestamp = Date.parse(left.recordedAt)
  const rightTimestamp = Date.parse(right.recordedAt)

  if (!Number.isNaN(leftTimestamp) && !Number.isNaN(rightTimestamp)) {
    return rightTimestamp - leftTimestamp
  }

  return right.recordedAt.localeCompare(left.recordedAt)
}

const isMoveOrTransferEvent = (
  event: Pick<LotEvent, 'eventType' | 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'toLocationType' | 'toLocationId' | 'metadata'>
): event is Pick<LotEvent, 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'toLocationType' | 'toLocationId' | 'metadata'> & {
  eventType: 'move' | 'transfer'
} => event.eventType === 'move' || event.eventType === 'transfer'

export const buildProcessingSiteRecentLotProvenanceMap = (
  lotIds: string[],
  events: Array<Pick<LotEvent, 'eventType' | 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'metadata'>>
): Record<string, ProcessingSiteRecentLotProvenance> => {
  const requestedLotIds = uniqueStrings(lotIds)
  const requestedLotIdSet = new Set(requestedLotIds)
  const provenanceByLotId: Record<string, ProcessingSiteRecentLotProvenance> = {}

  events
    .filter(isMoveOrTransferEvent)
    .slice()
    .sort(compareRecordedAtDesc)
    .forEach((event) => {
      const metadata = asObjectRecord(event.metadata)
      uniqueStrings(event.targetLotIds ?? []).forEach((lotId) => {
        if (!requestedLotIdSet.has(lotId) || provenanceByLotId[lotId]) {
          return
        }

        if (!event.fromLocationId && !metadata) {
          return
        }

        provenanceByLotId[lotId] = {
          eventType: event.eventType,
          recordedAt: event.recordedAt,
          fromLocationType: event.fromLocationType,
          fromLocationId: readOptionalString(event.fromLocationId),
          fromFarmId: readOptionalString(metadata?.fromFarmId),
          fromProductionUnitId: readOptionalString(metadata?.fromProductionUnitId)
        }
      })
    })

  return provenanceByLotId
}

export const buildProcessingSiteRecentLotProvenanceEntriesMap = (
  lotIds: string[],
  events: Array<Pick<LotEvent, 'eventType' | 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'metadata'>>,
  limit = 2
): Record<string, ProcessingSiteRecentLotProvenance[]> => {
  const normalizedLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0
  if (normalizedLimit <= 0) return {}

  const requestedLotIds = uniqueStrings(lotIds)
  const requestedLotIdSet = new Set(requestedLotIds)
  const entriesByLotId: Record<string, ProcessingSiteRecentLotProvenance[]> = {}

  events
    .filter(isMoveOrTransferEvent)
    .slice()
    .sort(compareRecordedAtDesc)
    .forEach((event) => {
      const metadata = asObjectRecord(event.metadata)

      if (!event.fromLocationId && !metadata) {
        return
      }

      uniqueStrings(event.targetLotIds ?? []).forEach((lotId) => {
        if (!requestedLotIdSet.has(lotId)) {
          return
        }

        const lotEntries = entriesByLotId[lotId] ?? []
        if (lotEntries.length >= normalizedLimit) {
          return
        }

        lotEntries.push({
          eventType: event.eventType,
          recordedAt: event.recordedAt,
          fromLocationType: event.fromLocationType,
          fromLocationId: readOptionalString(event.fromLocationId),
          fromFarmId: readOptionalString(metadata?.fromFarmId),
          fromProductionUnitId: readOptionalString(metadata?.fromProductionUnitId)
        })

        entriesByLotId[lotId] = lotEntries
      })
    })

  return entriesByLotId
}

export const buildProcessingSiteRecentLotProvenanceEntries = (
  lotId: string,
  events: Array<Pick<LotEvent, 'eventType' | 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'metadata'>>,
  limit = 3
): ProcessingSiteRecentLotProvenance[] => {
  const normalizedLotId = lotId.trim()
  if (!normalizedLotId || limit <= 0) return []

  return buildProcessingSiteRecentLotProvenanceEntriesMap([normalizedLotId], events, limit)[normalizedLotId] ?? []
}

export const buildProcessingSiteActivityEntries = (
  processingSiteId: string,
  lotIds: string[],
  events: Array<Pick<LotEvent, 'eventType' | 'recordedAt' | 'targetLotIds' | 'fromLocationType' | 'fromLocationId' | 'toLocationType' | 'toLocationId' | 'metadata'>>,
  limit = 3
): ProcessingSiteActivityEntry[] => {
  const normalizedProcessingSiteId = processingSiteId.trim()
  if (!normalizedProcessingSiteId || limit <= 0) return []

  const requestedLotIds = uniqueStrings(lotIds)
  const requestedLotIdSet = new Set(requestedLotIds)

  return events
    .filter(isMoveOrTransferEvent)
    .filter((event) => event.toLocationType === 'processing-site' && readOptionalString(event.toLocationId) === normalizedProcessingSiteId)
    .slice()
    .sort(compareRecordedAtDesc)
    .flatMap((event) => {
      const metadata = asObjectRecord(event.metadata)

      if (!event.fromLocationId && !metadata) {
        return []
      }

      return uniqueStrings(event.targetLotIds ?? [])
        .filter((lotId) => requestedLotIdSet.has(lotId))
        .map((lotId) => ({
          lotId,
          eventType: event.eventType,
          recordedAt: event.recordedAt,
          fromLocationType: event.fromLocationType,
          fromLocationId: readOptionalString(event.fromLocationId),
          fromFarmId: readOptionalString(metadata?.fromFarmId),
          fromProductionUnitId: readOptionalString(metadata?.fromProductionUnitId)
        }))
    })
    .slice(0, limit)
}

const buildOriginSummaryKey = (entry: Pick<ProcessingSiteActivityEntry, 'fromLocationType' | 'fromLocationId' | 'fromFarmId' | 'fromProductionUnitId'>): string => (
  [
    entry.fromLocationType ?? '',
    entry.fromLocationId ?? '',
    entry.fromFarmId ?? '',
    entry.fromProductionUnitId ?? ''
  ].join('::')
)

const compareOriginSummaryEntries = (
  left: ProcessingSiteActivityOriginSummaryEntry,
  right: ProcessingSiteActivityOriginSummaryEntry
): number => {
  if (right.totalCount !== left.totalCount) {
    return right.totalCount - left.totalCount
  }

  const leftTimestamp = Date.parse(left.latestRecordedAt)
  const rightTimestamp = Date.parse(right.latestRecordedAt)

  if (!Number.isNaN(leftTimestamp) && !Number.isNaN(rightTimestamp) && rightTimestamp !== leftTimestamp) {
    return rightTimestamp - leftTimestamp
  }

  return right.latestRecordedAt.localeCompare(left.latestRecordedAt)
}

export const buildProcessingSiteActivityOriginSummary = (
  entries: ProcessingSiteActivityEntry[],
  limit = 3
): ProcessingSiteActivityOriginSummaryEntry[] => {
  const normalizedLimit = Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : 0
  if (normalizedLimit <= 0 || entries.length === 0) {
    return []
  }

  const summaryByOrigin = new Map<string, ProcessingSiteActivityOriginSummaryEntry>()

  entries.forEach((entry) => {
    const key = buildOriginSummaryKey(entry)
    const existing = summaryByOrigin.get(key)

    if (!existing) {
      summaryByOrigin.set(key, {
        fromLocationType: entry.fromLocationType,
        fromLocationId: entry.fromLocationId,
        fromFarmId: entry.fromFarmId,
        fromProductionUnitId: entry.fromProductionUnitId,
        totalCount: 1,
        transferCount: entry.eventType === 'transfer' ? 1 : 0,
        moveCount: entry.eventType === 'move' ? 1 : 0,
        latestRecordedAt: entry.recordedAt
      })
      return
    }

    existing.totalCount += 1
    if (entry.eventType === 'transfer') {
      existing.transferCount += 1
    } else {
      existing.moveCount += 1
    }

    const existingTimestamp = Date.parse(existing.latestRecordedAt)
    const nextTimestamp = Date.parse(entry.recordedAt)
    if ((Number.isNaN(existingTimestamp) && !Number.isNaN(nextTimestamp)) || nextTimestamp > existingTimestamp) {
      existing.latestRecordedAt = entry.recordedAt
    }
  })

  return Array.from(summaryByOrigin.values())
    .sort(compareOriginSummaryEntries)
    .slice(0, normalizedLimit)
}

export const buildProcessingSiteActivityRecencySummary = (
  entries: ProcessingSiteActivityEntry[],
  now = Date.now()
): ProcessingSiteActivityRecencySummary => {
  const result: ProcessingSiteActivityRecencySummary = {
    totalEntries: entries.length,
    last24HoursCount: 0,
    last7DaysCount: 0,
    latestRecordedAt: null,
    isStale: false
  }

  if (entries.length === 0) {
    return result
  }

  const dayMs = 24 * 60 * 60 * 1000
  const weekMs = 7 * dayMs
  let latestTimestamp: number | null = null

  entries.forEach((entry) => {
    const recordedAtTimestamp = Date.parse(entry.recordedAt)
    if (Number.isNaN(recordedAtTimestamp)) {
      return
    }

    if (latestTimestamp === null || recordedAtTimestamp > latestTimestamp) {
      latestTimestamp = recordedAtTimestamp
      result.latestRecordedAt = entry.recordedAt
    }

    const ageMs = now - recordedAtTimestamp
    if (ageMs < 0) {
      return
    }

    if (ageMs <= dayMs) {
      result.last24HoursCount += 1
    }

    if (ageMs <= weekMs) {
      result.last7DaysCount += 1
    }
  })

  result.isStale = result.totalEntries > 0 && result.last7DaysCount === 0

  return result
}

export const getProcessingSiteActivityOriginRecencyBadge = (
  latestRecordedAt: string,
  now = Date.now()
): ProcessingSiteActivityOriginRecencyBadge => {
  const timestamp = Date.parse(latestRecordedAt)
  if (Number.isNaN(timestamp)) {
    return 'unknown'
  }

  const ageMs = now - timestamp
  if (ageMs < 0) {
    return 'unknown'
  }

  const dayMs = 24 * 60 * 60 * 1000
  const weekMs = 7 * dayMs

  if (ageMs <= dayMs) {
    return 'last24h'
  }

  if (ageMs <= weekMs) {
    return 'last7d'
  }

  return 'stale'
}

export const buildProcessingSiteActivityOriginRecencyBreakdown = (
  entries: ProcessingSiteActivityOriginSummaryEntry[],
  now = Date.now()
): ProcessingSiteActivityOriginRecencyBreakdown => {
  const result: ProcessingSiteActivityOriginRecencyBreakdown = {
    totalOrigins: entries.length,
    last24HoursCount: 0,
    last7DaysCount: 0,
    staleCount: 0,
    unknownCount: 0,
    hasStaleOrigins: false
  }

  entries.forEach((entry) => {
    const badge = getProcessingSiteActivityOriginRecencyBadge(entry.latestRecordedAt, now)
    if (badge === 'last24h') {
      result.last24HoursCount += 1
      return
    }

    if (badge === 'last7d') {
      result.last7DaysCount += 1
      return
    }

    if (badge === 'stale') {
      result.staleCount += 1
      result.hasStaleOrigins = true
      return
    }

    result.unknownCount += 1
  })

  return result
}